import { useEffect, useMemo, useState } from "react";
import { formatItalianDate, personName } from "../../format";
import { Badge, Btn, Modal } from "../../ui";
import { enqueueSisterBrowserNecrologi, previewSisterBrowserNecrologi } from "../../api";
import type { SisterEnqueuePreviewItem } from "../../api";
import { queueErrorMessage } from "./sister-queue-errors";
import { VisureQuota } from "./VisureQuota";

/** Anteprima selezionabile dell'accodamento SISTER: mostra i candidati
 * (finestra 90gg, con anno di nascita, non ancora in coda) e accoda solo
 * quelli spuntati dall'operatore. */
export function EnqueuePreviewModal({
  open,
  provincia,
  onClose,
  onDone,
}: {
  open: boolean;
  provincia: string | null;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const [items, setItems] = useState<SisterEnqueuePreviewItem[] | null>(null);
  const [stats, setStats] = useState<{ eligibleWithAnno: number; ineligibleNoAnno: number; alreadyInQueue: number; totalNecrologi: number } | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [queuedWaiting, setQueuedWaiting] = useState(0);
  const [clearQueued, setClearQueued] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setItems(null);
    setErr(null);
    setSelected(new Set());
    setQueuedWaiting(0);
    setClearQueued(false);
    previewSisterBrowserNecrologi({ limit: 100, provincia })
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setStats(res.stats);
        setQueuedWaiting(res.queuedWaiting ?? 0);
        // Preselezione: tutto ciò che è accodabile e non duplica una persona
        // già in coda. Gli indizi nel necrologio restano da verificare.
        setSelected(new Set(
          res.items.filter((i) => i.selectable && !i.duplicatePerson).map((i) => i.necrologioId),
        ));
      })
      .catch((e: unknown) => {
        if (!cancelled) setErr(queueErrorMessage(e, "Errore anteprima coda"));
      });
    return () => {
      cancelled = true;
    };
  }, [open, provincia]);

  const selectableIds = useMemo(
    () => (items ?? []).filter((i) => i.selectable && !i.duplicatePerson).map((i) => i.necrologioId),
    [items],
  );

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function enqueueSelected() {
    if (!selected.size) return;
    setBusy(true);
    setErr(null);
    try {
      const clear = clearQueued && queuedWaiting > 0;
      const res = await enqueueSisterBrowserNecrologi({
        necrologioIds: [...selected],
        provincia,
        clearQueued: clear,
      });
      const dupNote = res.skippedDuplicatePerson > 0
        ? ` ${res.skippedDuplicatePerson} saltati (persona già in coda).`
        : "";
      const clearNote = clear ? ` Coda svuotata: ${res.clearedQueued} ricerche in attesa tolte.` : "";
      onDone(`Coda SISTER: ${res.enqueued} accodati su ${selected.size} selezionati.${dupNote}${clearNote}`);
      onClose();
    } catch (e) {
      setErr(queueErrorMessage(e, "Errore accodamento"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Scegli chi cercare su SISTER${provincia ? ` · ${provincia}` : ""}`}
      width={720}
      footer={
        <div className="row gap-8" style={{ alignItems: "center", width: "100%" }}>
          <span className="muted" style={{ fontSize: 12 }}>
            {!items && !err
              ? "Caricamento candidati…"
              : items
                ? `${selected.size} selezionati su ${items.length} necrologi`
                : ""}
          </span>
          <div className="grow" />
          <Btn size="sm" variant="ghost" onClick={onClose} disabled={busy}>Annulla</Btn>
          <Btn
            size="sm"
            variant="primary"
            disabled={busy || !items || !selected.size}
            onClick={() => void enqueueSelected()}
          >
            {busy ? "Invio…" : !items ? "Carico…"
              : `${clearQueued && queuedWaiting > 0 ? "Svuota la coda e cerca" : "Cerca"} ${selected.size} ${selected.size === 1 ? "persona" : "persone"}`}
          </Btn>
        </div>
      }
    >
      {err && <div style={{ color: "var(--danger)", fontSize: 12, marginBottom: 8 }}>{err}</div>}
      {!items && !err && <div className="muted" style={{ fontSize: 13, padding: 12 }}>Caricamento candidati…</div>}
      {items && <VisureQuota selectedCount={selected.size} />}
      {items && queuedWaiting > 0 && (
        <section className="preview-box" role="status" aria-label="Ricerche già in coda" style={{ fontSize: 12, lineHeight: 1.6, margin: "8px 0", borderColor: "var(--warn)" }}>
          <div>
            <strong>{queuedWaiting === 1 ? "1 ricerca già in attesa" : `${queuedWaiting} ricerche già in attesa`} sull’estensione.</strong>{" "}
            L’estensione le esegue prima, in ordine: le persone che scegli qui vanno in fondo alla coda.
          </div>
          <label className="row gap-8" style={{ alignItems: "center", marginTop: 6, cursor: busy ? "default" : "pointer" }}>
            <input type="checkbox" checked={clearQueued} disabled={busy} onChange={(e) => setClearQueued(e.target.checked)} />
            Svuota la coda e cerca solo le persone selezionate
          </label>
          <div className="muted">
            Le ricerche tolte non sono perse: i necrologi degli ultimi 90 giorni tornano in questo elenco. Le visure già inoltrate restano in coda.
          </div>
        </section>
      )}
      {items && (
        <>
          <div className="row gap-8" style={{ alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
            <Btn size="sm" variant="ghost" disabled={busy} onClick={() => setSelected(new Set(selectableIds))}>
              Seleziona tutti
            </Btn>
            <Btn size="sm" variant="ghost" disabled={busy} onClick={() => setSelected(new Set())}>
              Nessuno
            </Btn>
            <Btn
              size="sm"
              variant="ghost"
              disabled={busy}
              title="Seleziona i necrologi in cui non è citato un coniuge superstite: è un’indicazione dal testo da verificare"
              onClick={() => setSelected(new Set(
                (items ?? []).filter((i) => i.selectable && !i.duplicatePerson && i.casaVuota).map((i) => i.necrologioId),
              ))}
            >
              Solo coniuge non citato
            </Btn>
            {stats && (
              <span className="muted" style={{ fontSize: 11 }}>
                ultimi 90 giorni: {stats.eligibleWithAnno} con anno di nascita · {stats.alreadyInQueue} già cercati o in coda · {stats.ineligibleNoAnno} senza anno di nascita (non cercabili)
              </span>
            )}
          </div>
          {items.length === 0 && (
            <div className="muted" style={{ fontSize: 13, padding: 12 }}>
              Nessun necrologio nuovo da cercare: quelli degli ultimi 90 giorni sono già stati cercati o sono in coda.
            </div>
          )}
          {items.length > 0 && (
            <div style={{ overflowX: "auto", maxHeight: 420, overflowY: "auto" }}>
              <table className="tbl" style={{ width: "100%", fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ padding: "6px 8px" }}></th>
                    <th style={{ textAlign: "left", padding: "6px 8px" }}>Decesso</th>
                    <th style={{ textAlign: "left", padding: "6px 8px" }}>Nome</th>
                    <th style={{ textAlign: "left", padding: "6px 8px" }}>Anno nascita (circa)</th>
                    <th style={{ textAlign: "left", padding: "6px 8px" }}>Luogo</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((i) => {
                    const disabled = !i.selectable || i.duplicatePerson;
                    return (
                      <tr
                        key={i.necrologioId}
                        style={{
                          borderTop: "1px solid var(--line)",
                          opacity: disabled ? 0.55 : 1,
                          cursor: disabled ? "default" : "pointer",
                        }}
                        title={i.warning ?? undefined}
                        onClick={() => !disabled && !busy && toggle(i.necrologioId)}
                      >
                        <td style={{ padding: "6px 8px" }}>
                          <input
                            type="checkbox"
                            disabled={disabled || busy}
                            checked={selected.has(i.necrologioId)}
                            onChange={() => toggle(i.necrologioId)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="mono" style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                          {formatItalianDate(i.dataDecesso)}
                        </td>
                        <td style={{ padding: "6px 8px" }}>
                          <span className="row gap-6" style={{ alignItems: "center", flexWrap: "wrap" }}>
                            <span style={{ fontWeight: 500 }}>{personName(i.nome)}</span>
                            {i.casaVuota && <Badge kind="warn" title="Il necrologio non cita un coniuge superstite. Verifica la situazione familiare e l’immobile.">Coniuge non citato · Da verificare</Badge>}
                            {i.vedovo && !i.casaVuota && <Badge kind="info">ved.</Badge>}
                            {i.duplicatePerson && <Badge kind="danger" title={i.warning ?? ""}>già in coda</Badge>}
                            {!i.selectable && <Badge kind="muted" title={i.warning ?? ""}>nome ambiguo</Badge>}
                          </span>
                        </td>
                        <td className="mono" style={{ padding: "6px 8px" }}>{i.annoNascita ?? "—"}</td>
                        <td style={{ padding: "6px 8px", color: "var(--text-2)" }}>
                          {i.comune || "—"}{i.provincia ? ` (${i.provincia})` : ""}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
